import { useState } from "react"
import { Platform, StyleSheet, Text, TouchableOpacity, View } from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import Slider from "@react-native-community/slider"

import { useAppTheme } from "src/hooks/useAppTheme"
import type { PlaybackControlsConfig } from "./types"

/**
 * Format seconds to time string (e.g., "1:23" or "1:23:45")
 */
const formatTime = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(seconds))
  const hours = Math.floor(safeSeconds / 3600)
  const minutes = Math.floor((safeSeconds % 3600) / 60)
  const secs = safeSeconds % 60

  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }
  return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
}

interface PlaybackControlsProps {
  config: PlaybackControlsConfig
}

/**
 * PlaybackControls - Built-in playback controls for video/audio players
 *
 * Features:
 * - Play/pause button
 * - Seek slider (optional, shown when duration is provided)
 * - Duration display
 * - Scrub time tooltip during seeking
 */
export function PlaybackControls({ config }: PlaybackControlsProps) {
  const {
    isPlaying,
    onTogglePlay,
    position = 0,
    duration = 0,
    isScrubbing = false,
    onScrubStart,
    onScrubChange,
    onScrubEnd,
  } = config

  const { theme } = useAppTheme()
  const { colors } = theme

  const [sliderWidth, setSliderWidth] = useState(0)
  const [localScrubTime, setLocalScrubTime] = useState<number | null>(null)

  const showScrubber = duration > 0 && onScrubEnd

  // Calculate scrub label position
  const SCRUB_LABEL_WIDTH = 72
  const scrubTime = localScrubTime ?? position
  const scrubRatio = Math.min(Math.max(duration > 0 ? scrubTime / duration : 0, 0), 1)
  const scrubLabelLeft = sliderWidth
    ? Math.min(
        Math.max(scrubRatio * sliderWidth - SCRUB_LABEL_WIDTH / 2, 0),
        Math.max(sliderWidth - SCRUB_LABEL_WIDTH, 0),
      )
    : 0

  const handleSlidingStart = () => {
    setLocalScrubTime(position)
    onScrubStart?.()
  }

  const handleValueChange = (value: number) => {
    setLocalScrubTime(value)
    onScrubChange?.(value)
  }

  const handleSlidingComplete = (value: number) => {
    setLocalScrubTime(null)
    onScrubEnd?.(value)
  }

  return (
    <View style={styles.container}>
      {/* Play/Pause Button */}
      <TouchableOpacity
        onPress={onTogglePlay}
        style={[styles.playButton, { backgroundColor: colors.buttonBackground }]}
        accessibilityRole="button"
        accessibilityLabel={isPlaying ? "Pause" : "Play"}
      >
        <MaterialIcons
          name={isPlaying ? "pause" : "play-arrow"}
          size={32}
          color={colors.buttonText}
        />
      </TouchableOpacity>

      {/* Scrubber (shown when duration is available) */}
      {showScrubber && (
        <PlaybackScrubberInternal
          duration={duration}
          position={position}
          isScrubbing={isScrubbing}
          scrubLabelLeft={scrubLabelLeft}
          scrubTime={scrubTime}
          localScrubTime={localScrubTime}
          setSliderWidth={setSliderWidth}
          handleSlidingStart={handleSlidingStart}
          handleValueChange={handleValueChange}
          handleSlidingComplete={handleSlidingComplete}
        />
      )}
    </View>
  )
}

/**
 * Scrubber row for placement above the controls row.
 * Includes a compact play/pause button to the left of the slider track.
 */
export function PlaybackScrubber({ config }: PlaybackControlsProps) {
  const {
    isPlaying,
    onTogglePlay,
    position = 0,
    duration = 0,
    isScrubbing = false,
    onScrubStart,
    onScrubChange,
    onScrubEnd,
    isFullscreen,
    onToggleFullscreen,
  } = config

  const [sliderWidth, setSliderWidth] = useState(0)
  const [localScrubTime, setLocalScrubTime] = useState<number | null>(null)
  const showScrubber = duration > 0 && onScrubEnd

  if (!showScrubber) return null

  const SCRUB_LABEL_WIDTH = 72
  const scrubTime = localScrubTime ?? position
  const scrubRatio = Math.min(Math.max(duration > 0 ? scrubTime / duration : 0, 0), 1)
  const scrubLabelLeft = sliderWidth
    ? Math.min(
        Math.max(scrubRatio * sliderWidth - SCRUB_LABEL_WIDTH / 2, 0),
        Math.max(sliderWidth - SCRUB_LABEL_WIDTH, 0),
      )
    : 0

  const handleSlidingStart = () => {
    setLocalScrubTime(position)
    onScrubStart?.()
  }

  const handleValueChange = (value: number) => {
    setLocalScrubTime(value)
    onScrubChange?.(value)
  }

  const handleSlidingComplete = (value: number) => {
    setLocalScrubTime(null)
    onScrubEnd?.(value)
  }

  return (
    <PlaybackScrubberInternal
      isPlaying={isPlaying}
      onTogglePlay={onTogglePlay}
      duration={duration}
      position={position}
      isScrubbing={isScrubbing}
      scrubLabelLeft={scrubLabelLeft}
      scrubTime={scrubTime}
      localScrubTime={localScrubTime}
      setSliderWidth={setSliderWidth}
      handleSlidingStart={handleSlidingStart}
      handleValueChange={handleValueChange}
      handleSlidingComplete={handleSlidingComplete}
      isFullscreen={isFullscreen}
      onToggleFullscreen={onToggleFullscreen}
    />
  )
}

// Internal helper to render the scrubber UI.
function PlaybackScrubberInternal({
  isPlaying,
  onTogglePlay,
  duration,
  position,
  isScrubbing,
  scrubLabelLeft,
  scrubTime,
  localScrubTime,
  setSliderWidth,
  handleSlidingStart,
  handleValueChange,
  handleSlidingComplete,
  isFullscreen,
  onToggleFullscreen,
}: {
  isPlaying?: boolean
  onTogglePlay?: () => void
  duration: number
  position: number
  isScrubbing: boolean
  scrubLabelLeft: number
  scrubTime: number
  localScrubTime: number | null
  setSliderWidth: (width: number) => void
  handleSlidingStart: () => void
  handleValueChange: (value: number) => void
  handleSlidingComplete: (value: number) => void
  isFullscreen?: boolean
  onToggleFullscreen?: () => void
}) {
  const { theme } = useAppTheme()
  const { colors } = theme

  const durationLabel = formatTime(duration)
  // Only show current time tooltip when actively scrubbing
  const showScrubLabel = isScrubbing || localScrubTime !== null
  const showFullscreenButton = Platform.OS === "web" && !!onToggleFullscreen

  return (
    <View style={styles.scrubberContainer}>
      <View style={styles.scrubberRow}>
        {/* Play/pause button — to the left of the slider track, no background */}
        {onTogglePlay !== undefined && (
          <TouchableOpacity
            onPress={onTogglePlay}
            style={styles.scrubberPlayButton}
            accessibilityRole="button"
            accessibilityLabel={isPlaying ? "Pause" : "Play"}
          >
            <MaterialIcons
              name={isPlaying ? "pause" : "play-arrow"}
              size={24}
              color={colors.buttonText}
            />
          </TouchableOpacity>
        )}

        <View
          style={styles.sliderWrapper}
          onLayout={(event) => setSliderWidth(event.nativeEvent.layout.width)}
        >
          {showScrubLabel && (
            <View
              style={[
                styles.scrubLabel,
                { left: scrubLabelLeft, backgroundColor: colors.buttonBackground },
              ]}
            >
              <Text style={[styles.scrubLabelText, { color: colors.buttonText }]}>
                {formatTime(scrubTime)}
              </Text>
            </View>
          )}
          <Slider
            style={styles.slider}
            minimumValue={0}
            maximumValue={Math.max(duration, 1)}
            value={position}
            minimumTrackTintColor={colors.buttonText}
            maximumTrackTintColor={colors.sectionSeparator}
            thumbTintColor={colors.buttonText}
            onSlidingStart={handleSlidingStart}
            onValueChange={handleValueChange}
            onSlidingComplete={handleSlidingComplete}
          />
        </View>

        {/* Duration */}
        <Text style={[styles.timeText, { color: colors.buttonText }]}>{durationLabel}</Text>

        {/* Fullscreen toggle (web only) */}
        {showFullscreenButton && (
          <TouchableOpacity
            onPress={onToggleFullscreen}
            style={styles.fullscreenButton}
            accessibilityRole="button"
            accessibilityLabel={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            <MaterialIcons
              name={isFullscreen ? "fullscreen-exit" : "fullscreen"}
              size={24}
              color={colors.buttonText}
            />
          </TouchableOpacity>
        )}
      </View>
    </View>
  )
}

/**
 * PlaybackLeftControls - Just the play/pause button for use in left controls slot
 */
export function PlaybackLeftControls({
  isPlaying,
  onTogglePlay,
}: {
  isPlaying: boolean
  onTogglePlay: () => void
}) {
  const { theme } = useAppTheme()
  const { colors } = theme

  return (
    <TouchableOpacity
      onPress={onTogglePlay}
      style={[styles.playButton, { backgroundColor: colors.buttonBackground }]}
      accessibilityRole="button"
      accessibilityLabel={isPlaying ? "Pause" : "Play"}
    >
      <MaterialIcons
        name={isPlaying ? "pause" : "play-arrow"}
        size={32}
        color={colors.buttonText}
      />
    </TouchableOpacity>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  playButton: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: "center",
    alignItems: "center",
  },
  scrubberContainer: {
    width: "100%",
  },
  scrubberPlayButton: {
    width: 32,
    height: 32,
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  scrubberRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    width: "100%",
  },
  sliderWrapper: {
    flex: 1,
    position: "relative",
  },
  slider: {
    width: "100%",
    height: 20,
  },
  timeText: {
    fontSize: 16,
    fontWeight: "600",
    textAlign: "right",
    flexShrink: 0,
  },
  scrubLabel: {
    position: "absolute",
    bottom: 56,
    width: 72,
    paddingVertical: 4,
    borderRadius: 8,
    alignItems: "center",
  },
  scrubLabelText: {
    fontSize: 16,
    fontWeight: "600",
  },
  fullscreenButton: {
    width: 32,
    height: 32,
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
})
